import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  GraduationCap, 
  CheckCircle2, 
  Clock,
  Phone,
  ArrowLeft,
  Users,
  Award,
  Calendar,
  BookOpen,
  Target,
  Briefcase
} from "lucide-react";

const courseInfo = {
  duration: "40-120 שעות",
  certification: "הסמכה ממשרד העבודה",
  format: "פרונטלי / היברידי",
  classes: "קבוצות קטנות"
};

const courseTypes = [
  {
    title: "קורס ממונה בטיחות בסיסי",
    hours: "40 שעות",
    description: "מיועד לארגונים קטנים עד בינוניים עם רמת סיכון נמוכה",
    includes: [
      "יסודות הבטיחות בעבודה",
      "חקיקה ותקנות",
      "זיהוי סיכונים",
      "ניהול תיק בטיחות"
    ]
  },
  {
    title: "קורס ממונה בטיחות מתקדם",
    hours: "80 שעות",
    description: "לארגונים בינוניים-גדולים או בעלי רמת סיכון בינונית",
    includes: [
      "כל תכני הקורס הבסיסי",
      "סקרי סיכונים מתקדמים",
      "ניהול חירום",
      "הדרכת עובדים"
    ],
    highlighted: true
  },
  {
    title: "קורס ממונה בטיחות מקיף",
    hours: "120 שעות",
    description: "לתעשייה, בנייה וארגונים בעלי סיכון גבוה",
    includes: [
      "כל תכני הקורסים הקודמים",
      "התמחות ענפית",
      "ניהול מערכות בטיחות",
      "פרויקט גמר"
    ]
  }
];

const curriculum = [
  { module: "מבוא לבטיחות", topics: "היסטוריה, עקרונות, תפקיד הממונה" },
  { module: "חקיקה ורגולציה", topics: "פקודת הבטיחות, תקנות, אכיפה" },
  { module: "זיהוי וניתוח סיכונים", topics: "שיטות זיהוי, מטריצות סיכון, הערכה" },
  { module: "ציוד מגן אישי", topics: "סוגים, בחירה, תחזוקה, הדרכה" },
  { module: "בטיחות אש", topics: "מניעה, גילוי, כיבוי, פינוי" },
  { module: "ארגונומיה", topics: "עמדות עבודה, מניעת פציעות, עיצוב" },
  { module: "חומרים מסוכנים", topics: "סיווג, טיפול, אחסון, חירום" },
  { module: "ניהול תיק בטיחות", topics: "תיעוד, דוחות, מעקב" }
];

const admissionRequirements = [
  "השכלה תיכונית מלאה (12 שנות לימוד)",
  "עברית ברמה גבוהה",
  "יכולת כתיבה ותקשורת",
  "מוטיבציה ללמידה",
  "רקע טכני - יתרון"
];

const careerOpportunities = [
  {
    title: "ממונה בטיחות בארגון",
    description: "תפקיד שכיר בחברה עם אחריות על כל נושאי הבטיחות"
  },
  {
    title: "יועץ בטיחות עצמאי",
    description: "מתן שירותי ייעוץ וממונה חיצוני למספר ארגונים"
  },
  {
    title: "מנהל בטיחות",
    description: "ניהול מחלקת בטיחות בארגון גדול"
  },
  {
    title: "מדריך בטיחות",
    description: "הכשרת עובדים והעברת הדרכות בטיחות"
  }
];

const faqs = [
  {
    question: "מה ההבדל בין הקורסים השונים?",
    answer: "הקורסים נבדלים בהיקף השעות ובעומק החומר. קורס 40 שעות מתאים לארגונים קטנים, קורס 80 שעות לארגונים בינוניים, וקורס 120 שעות נדרש לתעשייה ובנייה."
  },
  {
    question: "האם הקורס מוכר על ידי משרד העבודה?",
    answer: "כן, כל הקורסים שלנו מוכרים על ידי משרד העבודה ומקנים הסמכה רשמית לתפקיד ממונה בטיחות."
  },
  {
    question: "האם יש מבחן בסוף הקורס?",
    answer: "כן, בסיום הקורס יש מבחן הסמכה. יש צורך לעבור את המבחן כדי לקבל את התעודה. אנו מספקים הכנה מקיפה למבחן."
  },
  {
    question: "האם ניתן ללמוד גם מרחוק?",
    answer: "כן, אנו מציעים פורמט היברידי המשלב לימוד מרחוק עם מפגשים פרונטליים לחלקים המעשיים."
  }
];

export default function SafetyOfficersCourse() {
  return (
    <Layout>
      <Helmet>
        <title>קורס ממוני בטיחות | Adam Safety</title>
        <meta name="description" content="קורס הכשרת ממוני בטיחות עם הסמכה ממשרד העבודה. קורסים ב-40, 80 ו-120 שעות להתמחויות שונות. לימוד פרונטלי והיברידי." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <GraduationCap className="w-5 h-5" />
              <span className="text-sm font-medium">קורס הסמכה</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              קורס ממוני בטיחות
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              הכשרה מקצועית מקיפה לתפקיד ממונה בטיחות - 
              עם הסמכה רשמית ממשרד העבודה
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseInfo.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Award className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseInfo.certification}</span>
              </div>
              <div className="flex items-center gap-2 bg-primary-foreground/10 px-4 py-2 rounded-full">
                <Calendar className="w-4 h-4 text-accent" />
                <span className="text-primary-foreground text-sm">{courseInfo.format}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הרשמה לקורס
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Course Types */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              מסלולי הלימוד
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              בחרו את המסלול המתאים לצרכים שלכם
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {courseTypes.map((course, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-3xl p-8 ${
                  course.highlighted 
                    ? 'bg-primary text-primary-foreground ring-4 ring-accent' 
                    : 'bg-card border border-border'
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className={`text-sm px-3 py-1 rounded-full ${
                    course.highlighted 
                      ? 'bg-accent text-accent-foreground' 
                      : 'bg-accent/20 text-accent'
                  }`}>
                    {course.hours}
                  </span>
                </div>
                <h3 className={`text-xl font-bold mb-2 ${course.highlighted ? '' : 'text-foreground'}`}>
                  {course.title}
                </h3>
                <p className={`mb-6 text-sm ${course.highlighted ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {course.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {course.includes.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className={`w-4 h-4 flex-shrink-0 ${
                        course.highlighted ? 'text-accent' : 'text-accent'
                      }`} />
                      <span className={course.highlighted ? 'text-primary-foreground/90' : 'text-muted-foreground'}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button 
                    variant={course.highlighted ? "cta" : "outline"} 
                    className="w-full"
                  >
                    פרטים נוספים
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              תכנית הלימודים
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {curriculum.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-glass p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-5 h-5 text-accent" />
                  <h3 className="font-bold text-foreground">{item.module}</h3>
                </div>
                <p className="text-sm text-muted-foreground">{item.topics}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              הזדמנויות קריירה
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              לאן תוכלו להגיע עם הסמכת ממונה בטיחות
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {careerOpportunities.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-primary rounded-2xl p-6 text-primary-foreground"
              >
                <Briefcase className="w-8 h-8 text-accent mb-3" />
                <h3 className="font-bold mb-2">{career.title}</h3>
                <p className="text-primary-foreground/80 text-sm">{career.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Requirements */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                דרישות קבלה
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="card-glass p-8"
            >
              <ul className="space-y-4">
                {admissionRequirements.map((req, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Target className="w-4 h-4 text-accent" />
                    </div>
                    <span className="text-foreground">{req}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              שאלות נפוצות
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              מוכנים להתחיל קריירה בבטיחות?
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              הירשמו עכשיו וקבלו ייעוץ אישי לבחירת המסלול המתאים
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  הרשמה לקורס
                </Button>
              </Link>
              <Link to="/trainings">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  הדרכות נוספות
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}